import { z } from 'zod';
import { AllowlistSourceType } from '@soundxyz/utils/const';
import { AuctionTypeEnum } from '../../constants';
import { AuctionConfigurationErrors } from './errors';
import { numericNullish, numericString } from './helpers';

export const AddressSchema = z.object({
  address: z.string(),
  source: z.nativeEnum(AllowlistSourceType),
});

export const CollectorsOfReleasesSchema = z.object({
  allArtistCollaborations: z.boolean(),
  selectedReleaseIds: z.array(z.string()),
});

export const CollectorsOfArtistsSchema = z.object({
  allArtistCollectors: z.boolean(),
  allCollaboratingArtists: z.boolean(),
  selectedArtistIds: z.array(z.string()),
});

export type CollectorsOfReleases = z.infer<typeof CollectorsOfReleasesSchema>;
export type CollectorsOfArtists = z.infer<typeof CollectorsOfArtistsSchema>;

export const PresaleAllowListSchema = z.object({
  description: z.string().nullable(),
  addresses: z.array(AddressSchema),
  collectorsOfReleases: CollectorsOfReleasesSchema,
  collectorsOfArtists: CollectorsOfArtistsSchema,
});

export const AuctionConfigurationFields = z.object({
  auctionType: z.nativeEnum(AuctionTypeEnum).default(AuctionTypeEnum.OPEN_EDITION),
  spotifyTrackUri: z.string().nullish(),
  maxMintable: numericString,
  maxQuantity: numericNullish,
  minQuantity: numericNullish,
  presaleMaxMintsPerWallet: numericString,
  presaleMaxSupply: numericString,
  presalePricePerMint: numericString,
  presaleStartTime: z.date().nullable(),
  presaleEndTime: z.date().nullable(),
  publicEndTime: z.date().nullable(),
  publicCutoffTime: z.date().nullable(),
  publicMaxMintsPerWallet: numericString,
  publicPricePerMint: numericString,
  publicStartTime: z.date().nullable(),
  presaleAllowList: PresaleAllowListSchema,
});

export const AuctionConfigurationPriceLimits = z.object({
  publicMinPrice: z.number().optional(),
  publicMaxPrice: z.number().optional(),
  presaleMinPrice: z.number().optional(),
  presaleMaxPrice: z.number().optional(),
});

export const AuctionConfigurationMintQuantityLimits = z.object({
  minQuantity: z.number().optional(),
  maxQuantity: z.number().optional(),
  minPublicSaleMaxMintsPerWallet: z.number().optional(),
  maxPublicSaleMaxMintsPerWallet: z.number().optional(),
  minPresaleMaxMintsPerWallet: z.number().optional(),
  maxPresaleMaxMintsPerWallet: z.number().optional(),
});

export type AuctionConfigurationPriceLimits = (typeof AuctionConfigurationPriceLimits)['_input'];

export type AuctionConfigurationMintQuantityLimits =
  (typeof AuctionConfigurationMintQuantityLimits)['_input'];

export type AuctionConfigurationFields = (typeof AuctionConfigurationFields)['_input'];

const AuctionConfigurationTouchedFields = z.object({
  auctionType: z.boolean(),
  spotifyTrackUri: z.boolean(),
  maxMintable: z.boolean(),
  maxQuantity: z.boolean(),
  minQuantity: z.boolean(),
  presaleMaxMintsPerWallet: z.boolean(),
  presaleMaxSupply: z.boolean(),
  presalePricePerMint: z.boolean(),
  presaleStartTime: z.boolean(),
  presaleEndTime: z.boolean(),
  publicEndTime: z.boolean(),
  publicCutoffTime: z.boolean(),
  publicMaxMintsPerWallet: z.boolean(),
  publicPricePerMint: z.boolean(),
  publicStartTime: z.boolean(),
  presaleAllowList: z.object({
    description: z.boolean(),
    addresses: z.boolean(),
    collectorsOfReleases: z.boolean(),
    collectorsOfArtists: z.boolean(),
  }),
});

export type AuctionConfigurationTouchedFields =
  (typeof AuctionConfigurationTouchedFields)['_input'];

export const AuctionConfigurationSchema = z.object({
  fields: AuctionConfigurationFields,
  touched: AuctionConfigurationTouchedFields,
  errors: AuctionConfigurationErrors,
  hasPresaleMint: z.boolean(),
  hasGAEdition: z.boolean(),
  hasSpotifyPresave: z.boolean().nullish().transform(Boolean),
  isValid: z.boolean(),
  priceLimit: AuctionConfigurationPriceLimits,
  mintQuantityLimit: AuctionConfigurationMintQuantityLimits,
});

export type AuctionConfigurationState = (typeof AuctionConfigurationSchema)['_input'];
